import { useState } from "react";
import { ExternalLink, Plus, X } from "lucide-react";
import { membershipApi } from "../api";
import { MEMBERSHIP_PERMISSIONS } from "../permissions";
import type { DocumentStatus, MemberDocument } from "../types";
import { MembershipAccess } from "./MembershipAccess";
import { MembershipStatusBadge } from "./MembershipStatusBadge";

const PAGE_SIZE = 5;

const inp =
  "w-full rounded-2xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100";

function fmtDate(v?: string | null) {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return v; }
}

interface DocForm {
  documentName: string;
  documentStatus: DocumentStatus;
  fileName: string;
  fileUrl: string;
  remarks: string;
}

const EMPTY: DocForm = {
  documentName: "",
  documentStatus: "MISSING",
  fileName: "",
  fileUrl: "",
  remarks: "",
};

export function MembershipDocumentsPanel({
  memberId,
  documents,
  onReload,
}: {
  memberId: number;
  documents: MemberDocument[];
  onReload: () => void;
}) {
  const [page, setPage] = useState(0);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<DocForm>(EMPTY);
  const [saving, setSaving] = useState(false);

  const totalPages = Math.ceil(documents.length / PAGE_SIZE) || 1;
  const visible = documents.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const set = <K extends keyof DocForm>(k: K, v: DocForm[K]) =>
    setForm((p) => ({ ...p, [k]: v }));

  const save = async () => {
    if (!form.documentName.trim()) return;
    setSaving(true);
    try {
      await membershipApi.createDocument(memberId, form);
      setForm(EMPTY);
      setShowForm(false);
      onReload();
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-black text-slate-900">Documents</h2>
          <p className="mt-0.5 text-sm text-slate-500">{documents.length} document{documents.length !== 1 ? "s" : ""}</p>
        </div>
        <MembershipAccess permissions={[MEMBERSHIP_PERMISSIONS.EDIT, MEMBERSHIP_PERMISSIONS.MANAGE]}>
          <button
            type="button"
            onClick={() => setShowForm((v) => !v)}
            className="inline-flex items-center gap-1.5 rounded-2xl bg-emerald-700 px-3 py-2 text-xs font-black text-white hover:bg-emerald-800"
          >
            {showForm ? <X className="h-3.5 w-3.5" /> : <Plus className="h-3.5 w-3.5" />}
            {showForm ? "Cancel" : "Add Document"}
          </button>
        </MembershipAccess>
      </div>

      {showForm && (
        <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
          <p className="mb-3 text-xs font-black uppercase tracking-wide text-emerald-700">New Document</p>
          <div className="grid gap-3 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="label">Document Name *</label>
              <input value={form.documentName} onChange={(e) => set("documentName", e.target.value)} placeholder="e.g. Certificate of Incorporation" className={inp} />
            </div>
            <div>
              <label className="label">Status</label>
              <select value={form.documentStatus} onChange={(e) => set("documentStatus", e.target.value as DocumentStatus)} className={inp}>
                <option value="MISSING">Missing</option>
                <option value="SUBMITTED">Submitted</option>
                <option value="VERIFIED">Verified</option>
                <option value="REJECTED">Rejected</option>
                <option value="EXPIRED">Expired</option>
              </select>
            </div>
            <div>
              <label className="label">File Name</label>
              <input value={form.fileName} onChange={(e) => set("fileName", e.target.value)} placeholder="e.g. cert.pdf" className={inp} />
            </div>
            <div className="md:col-span-2">
              <label className="label">File URL</label>
              <input value={form.fileUrl} onChange={(e) => set("fileUrl", e.target.value)} placeholder="https://..." className={inp} />
            </div>
            <div className="md:col-span-2">
              <label className="label">Remarks</label>
              <input value={form.remarks} onChange={(e) => set("remarks", e.target.value)} placeholder="Any notes about this document" className={inp} />
            </div>
          </div>
          <button type="button" onClick={save} disabled={saving || !form.documentName.trim()} className="mt-4 rounded-2xl bg-emerald-700 px-4 py-2.5 text-sm font-black text-white hover:bg-emerald-800 disabled:opacity-60">
            {saving ? "Saving…" : "Save Document"}
          </button>
        </div>
      )}

      {documents.length === 0 ? (
        <div className="mt-4 rounded-2xl border border-dashed border-slate-200 py-8 text-center text-sm text-slate-400">No documents yet.</div>
      ) : (
        <>
          <div className="mt-4 overflow-x-auto rounded-2xl border border-slate-200">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs font-black uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="px-4 py-3">Document</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">File</th>
                  <th className="px-4 py-3">Uploaded</th>
                  <th className="px-4 py-3">Verified</th>
                  <th className="px-4 py-3">Verified By</th>
                  <th className="px-4 py-3">Remarks</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {visible.map((d) => (
                  <tr key={d.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-bold text-slate-900 whitespace-nowrap">{d.documentName}</td>
                    <td className="px-4 py-3"><MembershipStatusBadge value={d.documentStatus} /></td>
                    <td className="px-4 py-3">
                      {d.fileUrl ? (
                        <a href={d.fileUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 hover:underline">
                          <ExternalLink className="h-3 w-3" />
                          {d.fileName ?? "Open"}
                        </a>
                      ) : (
                        <span className="text-xs text-slate-400">{d.fileName ?? "—"}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600">{fmtDate(d.uploadedAt)}</td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-slate-600">{fmtDate(d.verifiedAt)}</td>
                    <td className="px-4 py-3 text-xs text-slate-600">{d.verifiedBy ?? "—"}</td>
                    <td className="px-4 py-3 text-xs text-slate-500">{d.remarks ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {totalPages > 1 && (
            <div className="mt-3 flex items-center justify-between">
              <span className="text-xs text-slate-400">{page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, documents.length)} of {documents.length}</span>
              <div className="flex gap-2">
                <button type="button" disabled={page === 0} onClick={() => setPage(p => p - 1)} className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-bold disabled:opacity-40">Prev</button>
                <button type="button" disabled={page + 1 >= totalPages} onClick={() => setPage(p => p + 1)} className="rounded-xl border border-slate-200 px-3 py-1.5 text-xs font-bold disabled:opacity-40">Next</button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
